import { useClassroomTeachersQuery } from '@innovamat/glow-api-client';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { ClassroomTeacher } from '../modules/teacher';

type Variables = {
  classroomId: string;
  organizationId: string;
  isPlatformEnabled: boolean;
};

type UseClassroomTeachersReturn = {
  data: ClassroomTeacher[];
  isLoading: boolean;
  queryKey: unknown[];
  refetch: () => void;
  invalidate: () => void;
};

const getClassroomTeachersQueryKey = (variables: Variables): unknown[] =>
  useClassroomTeachersQuery.getKey(variables);

const refetchClassroomTeachers = (
  queryClient: QueryClient,
  variables: Variables
): void => {
  queryClient.refetchQueries({
    queryKey: getClassroomTeachersQueryKey(variables),
  });
};

const invalidateClassroomTeachers = (
  queryClient: QueryClient,
  variables: Variables
): void => {
  queryClient.invalidateQueries({
    queryKey: getClassroomTeachersQueryKey(variables),
  });
};

type ClassroomTeachersQuery = {
  teachers?: ClassroomTeacher[];
};

function useClassroomTeachers(
  variables: Variables
): UseClassroomTeachersReturn {
  const queryClient = useQueryClient();
  const { data, isLoading } =
    useClassroomTeachersQuery<ClassroomTeachersQuery>(variables);

  return {
    data: data?.teachers || [],
    isLoading,
    queryKey: getClassroomTeachersQueryKey(variables),
    refetch: () => refetchClassroomTeachers(queryClient, variables),
    invalidate: () => invalidateClassroomTeachers(queryClient, variables),
  };
}

export {
  getClassroomTeachersQueryKey,
  invalidateClassroomTeachers,
  refetchClassroomTeachers,
  useClassroomTeachers,
};
export type { Variables as UseClassroomTeachersVariables };
